import {
  getStateConfigBySlug,
  type ChildSupportModelType,
  type ChildSupportStateSlug,
} from './schemas';

type Payer = 'you' | 'coparent' | 'none';

export type ChildSupportEstimateInput = {
  stateSlug: ChildSupportStateSlug;
  numChildren: number;
  yourMonthlyIncome: number;
  coparentMonthlyIncome: number;
  yourParentingTimePercent: number;
};

export type ChildSupportEstimateResult = {
  stateCode: string;
  stateName: string;
  modelType: ChildSupportModelType;
  payer: Payer;
  monthlyAmount: number;
  yourMonthlyShare: number;
  coparentMonthlyShare: number;
  notes: string[];
  requiresLegalReview: boolean;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getChildBucket(numChildren: number): '1' | '2' | '3' | '4' {
  if (numChildren <= 1) return '1';
  if (numChildren === 2) return '2';
  if (numChildren === 3) return '3';
  return '4';
}

function roundCurrency(value: number): number {
  return Math.max(0, Math.round(value));
}

function getLowIncomeReduction(income: number, reserve: number): number {
  if (income >= reserve * 1.25) return 1;
  return clamp(income / (reserve * 1.25), 0.35, 1);
}

export function estimateChildSupport(input: ChildSupportEstimateInput): ChildSupportEstimateResult {
  const state = getStateConfigBySlug(input.stateSlug);
  if (!state) {
    throw new Error('Invalid state');
  }

  const numChildren = clamp(Math.round(input.numChildren || 1), 1, 4);
  const childBucket = getChildBucket(numChildren);
  const yourIncome = Math.max(0, input.yourMonthlyIncome || 0);
  const coparentIncome = Math.max(0, input.coparentMonthlyIncome || 0);
  const combinedIncome = yourIncome + coparentIncome;
  const yourTime = clamp((input.yourParentingTimePercent || 50) / 100, 0.05, 0.95);
  const coparentTime = 1 - yourTime;

  const notes: string[] = [
    'Estimate only, not legal advice.',
    'Uses a simplified model to keep inputs short and non-laborious.',
  ];

  if (combinedIncome <= 0) {
    return {
      stateCode: state.code,
      stateName: state.name,
      modelType: state.modelType,
      payer: 'none',
      monthlyAmount: 0,
      yourMonthlyShare: 0,
      coparentMonthlyShare: 0,
      notes: [...notes, 'Both incomes were entered as $0.'],
      requiresLegalReview: state.requiresLegalReview,
    };
  }

  if (state.modelType === 'percentage_obligor') {
    const payer: Payer = yourIncome >= coparentIncome ? 'you' : 'coparent';
    const obligorIncome = payer === 'you' ? yourIncome : coparentIncome;
    const obligorTime = payer === 'you' ? yourTime : coparentTime;
    const basePct = state.obligorPercentages[childBucket];
    const raw = obligorIncome * basePct * state.stateFactor;
    const sharedCredit = clamp((obligorTime - 0.2) * 0.5, 0, 0.35);
    const lowIncomeReduction = getLowIncomeReduction(obligorIncome, state.lowIncomeReserve);
    const monthlyAmount = roundCurrency(raw * (1 - sharedCredit) * lowIncomeReduction);

    const yourMonthlyShare = payer === 'you' ? monthlyAmount : 0;
    const coparentMonthlyShare = payer === 'coparent' ? monthlyAmount : 0;

    notes.push(
      'State uses a simplified percentage-of-obligor model.',
      `Estimated percentage for ${numChildren} child(ren): ${(basePct * 100).toFixed(0)}%.`,
    );

    return {
      stateCode: state.code,
      stateName: state.name,
      modelType: state.modelType,
      payer,
      monthlyAmount,
      yourMonthlyShare,
      coparentMonthlyShare,
      notes,
      requiresLegalReview: state.requiresLegalReview,
    };
  }

  const basePct = state.basePercentages[childBucket];
  const baseObligation = combinedIncome * basePct * state.stateFactor;

  const yourIncomeWeight = yourIncome / combinedIncome;
  const coparentIncomeWeight = coparentIncome / combinedIncome;
  let yourShare = baseObligation * yourIncomeWeight;
  let coparentShare = baseObligation * coparentIncomeWeight;

  if (state.modelType === 'melson') {
    const yourCapacity = Math.max(0, yourIncome - state.lowIncomeReserve);
    const coparentCapacity = Math.max(0, coparentIncome - state.lowIncomeReserve);
    const totalCapacity = yourCapacity + coparentCapacity;
    if (totalCapacity > 0) {
      yourShare = baseObligation * (yourCapacity / totalCapacity);
      coparentShare = baseObligation * (coparentCapacity / totalCapacity);
    }
    notes.push('State uses a simplified Melson-style self-support reserve adjustment.');
  } else {
    notes.push('State uses a simplified income-shares model.');
  }

  const yourParentingCredit = baseObligation * yourTime * 0.5;
  const coparentParentingCredit = baseObligation * coparentTime * 0.5;
  const yourNet = yourShare - yourParentingCredit;
  const coparentNet = coparentShare - coparentParentingCredit;
  const transfer = (yourNet - coparentNet) / 2;

  let payer: Payer = 'none';
  if (transfer > 0) payer = 'you';
  if (transfer < 0) payer = 'coparent';

  const amountBeforeLowIncome = Math.abs(transfer);
  const payerIncome = payer === 'you' ? yourIncome : payer === 'coparent' ? coparentIncome : 0;
  const lowIncomeReduction = getLowIncomeReduction(payerIncome, state.lowIncomeReserve);
  const monthlyAmount = roundCurrency(amountBeforeLowIncome * lowIncomeReduction);

  return {
    stateCode: state.code,
    stateName: state.name,
    modelType: state.modelType,
    payer,
    monthlyAmount,
    yourMonthlyShare: payer === 'you' ? monthlyAmount : 0,
    coparentMonthlyShare: payer === 'coparent' ? monthlyAmount : 0,
    notes,
    requiresLegalReview: state.requiresLegalReview,
  };
}
